import { FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useQuery, UseQueryExecute } from 'urql';
import { BpEventType } from '../components/EventsList/EventsList';
import { eventColors } from '../utils/dateCalculations';
import { isNotEmpty } from '../utils/type-guards';
import { useAuthClaims } from './useAuthClaims';
import { CONNECT_DATE_TEMPLATE } from 'utils/connectLib';
import { usePermissionChecker } from './usePermissionChecker';

// TODO: check long term how we can integrate them into pages or if we can make custom resolvers for them

export const CalendarEventMapCalendarEventsToBpEventTypeDocument = graphql(`
  fragment CalendarEventMapCalendarEventsToBpEventTypeFields on Query {
    ...PermissionCheckerFields
    todaysEventData: calendarEvents(options: { sort: [{ start: ASC }], limit: 500 }, where: $calendarEventWhere) {
      uuid
      __typename
      categories
      guestsAllowed
      maxAttendees
      virtualLocations {
        uuid
        features
        token
        running
        attendeesConnection {
          edges {
            properties {
              withVideo
            }
          }
        }
      }
      owner {
        uuid
      }
      classbookNoteContentType
      classbookHomework
      classbookHomeworkContentType
      classbookNote
      classShortNames
      locations {
        uuid
      }
      attendeesConnection {
        __typename
        edges {
          __typename
          properties {
            classbookComment
          }
          node {
            uuid
            __typename
            displayName
          }
        }
      }
      description
      title
      duration
      keywords
      locale
      status
      start
      holderConnection {
        edges {
          node {
            __typename
            ... on Organization {
              uuid
              displayName
            }
            ... on Group {
              uuid
              displayName
              type
            }
            ... on Profile {
              uuid
              displayName
            }
          }
        }
      }
    }
  }
`);

const GroupOrCourseCalendarEventsQueryDocument = graphql(`
  query GroupOrCourseCalendarEventsData($calendarEventWhere: CalendarEventWhere!, $profile: ID!) {
    ...CalendarEventMapCalendarEventsToBpEventTypeFields
    ...PermissionCheckerFields
  }
`);

const BirthdayEventsQueryDocument = graphql(`
  query BirthdayEventsData($courseUuid: ID!, $groupUuid: ID!) {
    birthdayEvents(courseUuid: $courseUuid, groupUuid: $groupUuid) {
      uuid
      __typename
      start
      categories
      title
      classShortNames
    }
  }
`);

export const useCourseCalendarEvents = (uuid: string): BpEventType[] => {
  const events = useGroupOrCourseCalendarEvents([uuid]);
  const birthdays = useBirthdays(undefined, uuid);
  return [...events, ...birthdays].sort((a, b) => {
    const startA = a.start.unix();
    const startB = b.start.unix();
    return startA - startB;
  });
};

export const useGroupOrCoursesOnlineEvents = (uuids: string[]): BpEventType[] => {
  const events = useGroupOrCourseCalendarEvents(uuids, true);
  return events.sort((a, b) => {
    const startA = a.start.unix();
    const startB = b.start.unix();
    return startA - startB;
  });
};

export const useGroupCalendarEvents = (uuid: string) => {
  const events = useGroupOrCourseCalendarEvents([uuid]);
  const birthdays = useBirthdays(uuid, undefined);
  return [...events, ...birthdays].sort((a, b) => {
    const startA = a.start.unix();
    const startB = b.start.unix();
    return startA - startB;
  });
};

const useGroupOrCourseCalendarEvents = (uuid: string[], onlyOnline: boolean = false) => {
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const [{ data }] = useQuery({
    query: GroupOrCourseCalendarEventsQueryDocument,
    variables: {
      profile: profileUuid,
      calendarEventWhere: {
        holderConnection: {
          Group: {
            node: {
              uuid_IN: uuid,
            },
          },
        },
        ...(onlyOnline
          ? {
              virtualLocations_SOME: {
                running: true,
              },
            }
          : undefined),
      },
    },
  });
  const permissions = usePermissionChecker(data);

  return mapCalendarEventsToBpEventType(
    data,
    pimAuthClaims.getProfile().uuid,
    permissions?.isOrganizationAdmin(organization) ?? false,
  );
};

export const useBirthdays = (groupUuid?: string, courseUuid?: string): BpEventType[] => {
  const [{ data }] = useQuery({
    query: BirthdayEventsQueryDocument,
    variables: {
      courseUuid: courseUuid ?? '',
      groupUuid: groupUuid ?? '',
    },
  });

  return (
    data?.birthdayEvents
      .map((bE) => ({
        uuid: bE.uuid,
        guestsAllowed: false,
        virtualLocationsCount: 0,
        virtualLocations: [],
        owner: false,
        locationsCount: 0,
        attendees: [],
        start: dayjs(bE.start),
        color: eventColors(bE.categories ?? [], true),
        type: 'birthday',
        categories: [],
        title: bE.title ?? '',
        keywords: [],
        classShortNames: bE.classShortNames,
      }))
      .filter(isNotEmpty) ?? []
  );
};

export const getRunningPlannedMeetingsFilter = ({
  organizationUuid,
  profileUuid,
}: {
  organizationUuid: string;
  profileUuid: string;
}) => [
  {
    OR: [
      {
        virtualLocations_SOME: {
          running: true,
        },
      },
      {
        daysConnection_SOME: { node: { date: dayjs().format(CONNECT_DATE_TEMPLATE) } },
      },
    ],
  },
  {
    OR: [
      {
        holderConnection: {
          Organization: {
            node: { uuid: organizationUuid },
          },
        },
      },
      {
        attendees_SOME: {
          uuid: profileUuid,
        },
      },
      {
        owner: {
          uuid: profileUuid,
        },
      },
    ],
  },
];

export const useRunningPlannedMeetings = ({
  profileUuid,
  query,
  refetch,
}: {
  profileUuid: string;
  query: FragmentType<typeof CalendarEventMapCalendarEventsToBpEventTypeDocument> | undefined;
  refetch: UseQueryExecute;
}): { runningMeetings: BpEventType[]; plannedMeetings: BpEventType[]; refetchEvents: () => void } => {
  const { pimAuthClaims } = useAuthClaims();
  const fragment = getFragmentData(CalendarEventMapCalendarEventsToBpEventTypeDocument, query);
  const permissions = usePermissionChecker(fragment);
  const organization = pimAuthClaims.getOrganization();

  const { runningMeetings, plannedMeetings } = useMemo(() => {
    const plannedMeetings: BpEventType[] = [];
    const runningMeetings: BpEventType[] = [];

    mapCalendarEventsToBpEventType(query, profileUuid, permissions?.isOrganizationAdmin(organization) ?? false)
      .filter(
        (e) =>
          e.virtualLocationsCount > 0 &&
          ((dayjs().add(1, 'days').isAfter(e.start) && dayjs().subtract(Number(e.duration), 's').isBefore(e.start)) ||
            e.virtualLocations.find((v) => v.running)?.running),
      )
      .forEach((e) => {
        if (e.virtualLocations.find((v) => v.running)) {
          runningMeetings.push(e);
        } else {
          plannedMeetings.push(e);
        }
      });

    return { runningMeetings, plannedMeetings };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, refetch]);

  return {
    runningMeetings: runningMeetings,
    plannedMeetings: plannedMeetings,
    refetchEvents: () => refetch({ requestPolicy: 'network-only' }),
  };
};

export const mapCalendarEventsToBpEventType = (
  query: FragmentType<typeof CalendarEventMapCalendarEventsToBpEventTypeDocument> | undefined,
  currentProfileUuid: string,
  isEventAdmin: boolean,
) => {
  const calendarEvents = getFragmentData(CalendarEventMapCalendarEventsToBpEventTypeDocument, query);
  return (calendarEvents?.todaysEventData ?? []).map((e) => {
    const eventColor = eventColors(e.categories ?? []);

    const holderNode = e.holderConnection.edges.find((h) => (h.node.uuid ?? '').length > 0)?.node;

    return {
      uuid: e.uuid,
      guestsAllowed: e.guestsAllowed ?? true,
      maxAttendees: e.maxAttendees ?? 0,
      virtualLocationsCount: e.virtualLocations.length,
      virtualLocations:
        e.virtualLocations.map((v) => ({
          uuid: v.uuid,
          features: v.features ?? [],
          uri: import.meta.env.VITE_APP_REDIRECT_URI + '/join/' + v.token,
          running: v.running,
          token: v.token,
          currentAttendees: v.attendeesConnection.edges.length,
          currentVideoAttendees: v.attendeesConnection.edges.filter((e) => e.properties.withVideo).length,
        })) ?? [],
      owner: e.owner.uuid === currentProfileUuid || isEventAdmin,
      locationsCount: e.locations.length,
      classShortNames: e.classShortNames,
      classbookNote: e.classbookNote ?? undefined,
      classbookNoteContentType: e.classbookNoteContentType ?? undefined,
      classbookHomework: e.classbookHomework ?? undefined,
      classbookHomeworkContentType: e.classbookHomeworkContentType ?? undefined,
      attendees: e.attendeesConnection.edges.map((p) => {
        return { ...p.properties, ...p.node, displayName: p.node.displayName ?? '' };
      }),
      start: dayjs(e.start),
      color: eventColor,
      type: 'event',
      categories: e.categories ?? [],
      description: e.description ?? '',
      title: e.title ?? '',
      duration: e.duration ?? undefined,
      keywords: e.keywords ?? [],
      locale: e.locale ?? '',
      status: e.status ?? '',
      originDisplayName: holderNode?.displayName ?? '',
      originUuid: holderNode?.uuid ?? '',
      originType: holderNode?.__typename === 'Group' ? holderNode.type : holderNode?.__typename,
    };
  });
};
